import { useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import Edit from '@mui/icons-material/Edit'
import { Button, Link, Stack, Tooltip, Typography } from '@mui/material'
import { useStytchIsAuthorized } from '@stytch/react/b2b'
import useLocationHelpers from '@/hooks/useLocationHelpers'
import useOverlay from '@/hooks/useOverlay'
import { useGetDocuments } from '@/service-library/hooks/documents'
import {
  useDeleteWorkflowState,
  useUpdateWorkflowState,
} from '@/service-library/hooks/workflow-states'
import queryKeys from '@/service-library/query-keys'
import { themeColorOptions } from '@/theme/usePixydocsTheme'
import { showErrorSnackbar, showSuccessSnackbar } from '@/utils/snackbars'
import CodeEditorDialog from '@/components/code-editor/CodeEditorDialog'
import PixyMarkdown from '@/components/markdown/PixyMarkdown'
import PixyColorPicker from '@/components/PixyColorPicker'
import SettingsRow from '@/components/shared/settings/SettingsRow'
import SettingsSection from '@/components/shared/settings/SettingsSection'
import { useSelectedWorkflowContext } from '@/components/workflows/SelectedWorkflowProvider'
import DeleteWorkflowStateDialog from './DeleteWorkflowStateDialog'
import WorkflowStateDocumentViews from './WorkflowStateDocumentViews'
import WorkflowStateNameField from './WorkflowStateNameField'
import { useWorkflowStatePageContext } from './WorkflowStatePage'
import WorkflowStateRelevantFields from './WorkflowStateRelevantFields'

export default function WorkflowStateSettings() {
  const {
    workflowState,
    queryKey: detailQueryKey,
    listQueryKey,
  } = useWorkflowStatePageContext()
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { appendCurrentParamsToUrl } = useLocationHelpers()
  const { isAuthorized: canEditWorkflowState } = useStytchIsAuthorized(
    'pd.project',
    'edit',
  )
  const { selectedWorkflow } = useSelectedWorkflowContext()

  const { updateWorkflowState } = useUpdateWorkflowState({
    sideEffectQueryKeys: [
      queryKeys.workflows.lists(), // Update it in case they've already fetched it on other pages
    ],
    detailQueryKey,
    listQueryKey,
    onError: () => {
      showErrorSnackbar(
        'Unable to update workflow state. Please try again later.',
      )
    },
    onSuccess: () => {
      showSuccessSnackbar('Description Updated')
    },
  })

  // Name and color save on their own, without the description's toast.
  const { updateWorkflowState: updateWorkflowStateDetails } =
    useUpdateWorkflowState({
      sideEffectQueryKeys: [queryKeys.workflows.lists()],
      detailQueryKey,
      listQueryKey,
      onError: () => {
        showErrorSnackbar(
          'Unable to update workflow state. Please try again later.',
        )
      },
    })

  const deleteOverlay = useOverlay()

  const { documents, isPending } = useGetDocuments({
    filters: {
      workflow_states__id: workflowState.id,
      limit: '1',
      fields__only: 'id',
    },
  })

  const { deleteWorkflowState } = useDeleteWorkflowState({
    listQueryKey,
    sideEffectQueryKeys: [queryKeys.workflows.lists()],
    onError: () => {
      showErrorSnackbar(
        'Unable to delete workflow state. Please try again later.',
      )
    },
    onSuccess: () => {
      showSuccessSnackbar('Workflow State Deleted')
    },
  })

  const handleDeleteWorkflowState = () => {
    deleteWorkflowState(workflowState.id)
    navigate(
      appendCurrentParamsToUrl(
        `/projects/${projectId}/settings/workflow-states`,
      ),
    )
  }

  const isInitialState =
    selectedWorkflow?.initial_workflow_state_id === workflowState.id

  const hasDocuments = documents.length > 0

  const descriptionOverlay = useOverlay()
  const [descriptionValue, setDescriptionValue] = useState(
    workflowState.description || '',
  )

  const canEditThisState =
    canEditWorkflowState &&
    !['available_for_training', 'training_batch'].includes(workflowState.code)

  return (
    // The Events tab uses the editor's full width, but these settings read
    // better at the width of a SettingsPage form: its `md` Container, whose
    // max width is the theme's `md` breakpoint.
    <Stack
      spacing={5}
      sx={{ maxWidth: (theme) => theme.breakpoints.values.md }}
    >
      {canEditThisState && (
        <SettingsSection title="Details">
          <SettingsRow
            label="Name"
            description="Shown in the states list and on documents in this state."
            htmlFor="workflow-state-name"
          >
            <WorkflowStateNameField
              id="workflow-state-name"
              workflowState={workflowState}
              updateWorkflowState={updateWorkflowStateDetails}
            />
          </SettingsRow>
          <SettingsRow
            label="Color"
            description="Used for grouping or recognizing states by color."
          >
            <PixyColorPicker
              colors={themeColorOptions}
              selectedColor={workflowState.color || 'blue'}
              setSelectedColor={(color) => {
                if (color === workflowState.color) return
                updateWorkflowStateDetails({ ...workflowState, color })
              }}
            />
          </SettingsRow>
        </SettingsSection>
      )}

      <WorkflowStateDocumentViews
        workflowState={workflowState}
        detailQueryKey={detailQueryKey}
      />

      <SettingsSection
        title="Description"
        description="Shown in places across the app. Supports markdown."
        actions={
          <Button
            variant="text"
            startIcon={<Edit />}
            onClick={descriptionOverlay.open}
          >
            Edit
          </Button>
        }
      >
        <SettingsRow>
          {workflowState?.description ? (
            <PixyMarkdown>{workflowState?.description}</PixyMarkdown>
          ) : (
            <Typography variant="body2" color="text.secondary">
              <i>Click &quot;Edit&quot; to add a description.</i>
            </Typography>
          )}
        </SettingsRow>
      </SettingsSection>

      <WorkflowStateRelevantFields
        excludedFieldsIds={workflowState.excluded_project_grid_fields_ids || []}
        detailQueryKey={detailQueryKey}
        workflowState={workflowState}
      />

      {canEditThisState && (
        <SettingsSection title="Danger zone" tone="danger">
          <SettingsRow
            label="Delete this workflow state"
            description="Removes the state from this workflow. This can't be undone."
          >
            <Tooltip
              title={
                <>
                  {isInitialState && (
                    <div>Cannot delete the initial workflow state.</div>
                  )}

                  {hasDocuments && (
                    <div>
                      Cannot delete workflow states that have documents assigned
                      to them.
                    </div>
                  )}
                </>
              }
              slotProps={{
                popper: {
                  sx: {
                    display:
                      hasDocuments || isInitialState ? undefined : 'none',
                  },
                },
              }}
            >
              {/* Tooltip automatically hides if it's main child is disabled, so we need to wrap it with a span */}
              <span>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={deleteOverlay.open}
                  disabled={isPending || hasDocuments || isInitialState}
                >
                  Delete state
                </Button>
              </span>
            </Tooltip>
          </SettingsRow>
        </SettingsSection>
      )}

      <CodeEditorDialog
        overlay={descriptionOverlay}
        title="Description"
        value={descriptionValue || ''}
        setValue={setDescriptionValue}
        wrapEnabledInitially
        characterLimit={8000}
        onSave={(updatedDescription) => {
          updateWorkflowState({
            ...workflowState,
            description: updatedDescription,
          })
          descriptionOverlay.close()
        }}
        description={
          <Typography variant="body2" color="text.secondary">
            This description will be available in various places throughout the
            app. Workflow state descriptions accept{' '}
            <Link
              href="https://www.markdownguide.org/cheat-sheet/"
              target="_blank"
              rel="noreferrer"
            >
              markdown formatting
            </Link>
            .
          </Typography>
        }
        mode="markdown"
      />
      <DeleteWorkflowStateDialog
        overlay={deleteOverlay}
        workflowState={workflowState}
        onDelete={handleDeleteWorkflowState}
      />
    </Stack>
  )
}
